import { PropsWithChildren } from 'react';

import { cn } from '@/shared/helpers';

/**
 * `<PageContent>` equivalent for list pages whose body *is* a data table. Sits
 * as a direct child of `<Page>` (in place of `<PageContent>`), owning its own
 * page inset, vertical rhythm, and sidebar-inset bottom-bleed so the
 * pagination footer reaches the sidebar edge flush. Bleeds the `DataTable` and
 * pagination children out to the page edges, pins pagination to the bottom of
 * the page, and frames the table with top/bottom borders so it reads as a card
 * meeting the sidebar-inset's rounded corners.
 *
 * For detail-page tables that live inside a `Card`, use `DataTableCard*`
 * helpers instead — they keep the table inside the card padding.
 *
 * ```tsx
   <Page>
     <Header>...</Header>
     <DataTablePageContent>
       <DataTableToolbar>
          <DataTableViewMenu />
         <DataTableFilterMenu />
         <DataTableRefreshMenu />
       </DataTableToolbar>
       <DataTable />
       <DataTablePagination />
     </DataTablePageContent>
   </Page>
 * ```
 */
export function DataTablePageContent({ children }: PropsWithChildren) {
  return (
    <div
      className={cn(
        // As a direct child of Page: own the horizontal inset and the 16px gap below the header,
        // fill remaining viewport height, no bottom inset — the
        // pagination footer sits flush to the sidebar-inset's rounded bottom edge.
        'flex min-h-0 flex-1 flex-col px-(--page-inset) pt-4',
        '*:data-[slot="data-table-toolbar"]:mb-4',

        // Bleed the data table to the page edges so its card surface meets the sidebar-inset
        // rounded corners; restore cell alignment with the toolbar above via compensating
        // first/last padding. When a vertical scrollbar is present, reduce only the last cell's
        // right padding by the measured scrollbar width so content stays aligned with the toolbar.
        // Only a top border on the table — the pagination's own top border serves as the visual
        // bottom of the card.
        '*:data-[slot="data-table"]:-mx-(--page-inset) *:data-[slot="data-table"]:min-h-0',
        '*:data-[slot="data-table"]:flex-1 *:data-[slot="data-table"]:border-t *:data-[slot="data-table"]:bg-card',
        '[&_td]:first:pl-(--page-inset) [&_th]:first:pl-(--page-inset)',
        '[&_th]:last:pr-[max(0px,calc(var(--page-inset)-var(--data-table-scrollbar-width,0px)))]',
        '[&_td]:last:pr-[max(0px,calc(var(--page-inset)-var(--data-table-scrollbar-width,0px)))]',

        // Pagination footer: bleed horizontally so its top border spans the sidebar-inset
        // edge-to-edge. w-auto overrides the component's own w-full so the negative horizontal
        // margins actually expand the box.
        '*:data-[slot="pagination"]:-mx-(--page-inset) *:data-[slot="pagination"]:px-(--page-inset)',
        '*:data-[slot="pagination"]:w-auto *:data-[slot="pagination"]:border-t',
      )}
    >
      {children}
    </div>
  );
}

export function DataTableToolbar({ children }: PropsWithChildren) {
  return (
    <div
      data-slot="data-table-toolbar"
      className={cn(
        'grid grid-cols-[1fr_auto] gap-x-2 gap-y-4 *:col-span-full',
        '*:data-[slot="actions"]:col-span-1 *:data-[slot="refresh"]:col-span-1 *:data-[slot="title"]:col-span-1',
        '*:data-[slot="actions"]:col-start-2 *:data-[slot="refresh"]:col-start-2',
        // Keep title full-width when no right-side controls are rendered.
        '*:data-[slot="title"]:has-[+_[data-slot="data-table"]]:col-span-full',
        // Contract whichever panel appears before a right-side slot so the pair shares one row.
        '*:has-[+_[data-slot="actions"]]:col-span-1 *:has-[+_[data-slot="refresh"]]:col-span-1',
      )}
    >
      {children}
    </div>
  );
}
